import { StyledAvatar } from "components/ShareButton/ShareSandbox/blocks/StyledAvatar";
import { env } from "environment-interface/env";
import { useEditor } from "features/editor/Feature";
import type { SandboxEditor } from "features/types/editor";
import { getSandboxTitleAndDescription, withBasePath } from "isomorphic/utils";
import {
  Box,
  styled,
  Icon,
  Stack,
  Button,
  ButtonBadge,
  Text,
} from "prism-react";
import React, { useEffect, useState } from "react";
import useMeasure from "react-use-measure";

import { TemplateIcon } from "components/TemplateIcon";
import { paths } from "utils/router";
import { executeVSCodeCommand } from "utils/vscode";

import { useCanEditSandboxInfo } from "./useCanEditSandbox";

const StyledDT = styled("dt", {
  display: "flex",
  alignItems: "center",
  color: "var(--vscode-descriptionForeground, $neutral-fg-subtle)",
});

const StyledDD = styled("dd", {
  marginInlineStart: 0,
  display: "flex",
  alignItems: "center",
  color: "var(--vscode-descriptionForeground, $neutral-fg-subtle)",
});

const StyledA = styled("a", {
  transition: "color $fast",
  marginInlineStart: 0,
  display: "flex",
  alignItems: "center",
  textDecoration: "none",
  color: "var(--vscode-textLink-foreground, $neutral-fg-subtle)",
  "&:hover": {
    color: "var(--vscode-textLink-activeForeground, $neutral-fg-high)",
  },
});

const StyledDItem = styled("div", {
  display: "flex",
  gap: "$1",
});

const StyledDL = styled("dl", {
  display: "flex",
  gap: "$4",
  margin: 0,
  width: "100%",
});

const StyledTitle = styled("h5", {
  margin: 0,
  fontWeight: "$medium",
  fontSize: "13px",
  color: "$neutral-fg-subtle",
  truncateText: true,
});

export const SandboxInfo: React.FC<{
  sandbox: SandboxEditor;
  onEditClick?: () => void;
}> = ({ sandbox, onEditClick }) => {
  /**
   * Measure the container to define wether we want to show the full list of "tags"
   * or the author only.
   */
  const [ref, { width }] = useMeasure();
  const [small, setSmall] = useState(false);
  const canEditSandbox = useCanEditSandboxInfo();
  const project = useEditor();

  const { forkedTemplate, forkedFromSandbox, forkedTemplateSandbox } = sandbox;

  // It is always there
  const title = project.type === "sandbox" ? project.title : "";
  const { description } = getSandboxTitleAndDescription(sandbox);

  useEffect(() => {
    setSmall(width < 120);
  }, [width]);

  const git = sandbox.git;

  return (
    <Box
      ref={ref}
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "$2",
        width: "100%",
      }}
      selectable
    >
      {/* Title */}
      {title ? (
        <Box
          css={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack
            css={{ alignItems: "center", width: "100%" }}
            gap={2}
            horizontal
          >
            <TemplateIcon
              height={20}
              iconUrl={
                sandbox.customTemplate?.iconUrl ||
                sandbox.forkedTemplate?.iconUrl
              }
              templateName={sandbox.title}
              width={20}
            />

            <StyledTitle>{title}</StyledTitle>
            {onEditClick && canEditSandbox ? (
              <Button
                css={{
                  marginTop: "-$2",
                  marginRight: "-$2",
                  marginBottom: "-$2",
                  marginLeft: "auto",
                }}
                icon="edit"
                onClick={() => {
                  executeVSCodeCommand("codesandbox.openCsbTab", {
                    type: "SANDBOX_INFO",
                    isCloud: project.type === "sandbox" && project.isCloud,
                  });
                  onEditClick?.();
                }}
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {(description || forkedFromSandbox) && (
        <Stack gap={1}>
          {/* Description */}
          {description && (
            <Box
              css={{
                wordBreak: "break-word",
                lineHeight: 1.4,
                marginBottom: "$4",
              }}
              selectable
            >
              {description}

              {git && (
                <Box css={{ marginTop: "$3", width: "fit-content" }}>
                  <ButtonBadge
                    icon="github"
                    onClick={() => {
                      let url = `https://github.com/${git.username}/${git.repo}`;

                      if (git.branch) {
                        url += `/tree/${git.branch}`;
                      }
                      if (git.path) {
                        url += `/${git.path}`;
                      }
                      window.open(url, "_blank");
                    }}
                  >
                    {git.username}/{git.repo}
                  </ButtonBadge>
                </Box>
              )}
            </Box>
          )}

          {forkedFromSandbox ? (
            <Stack gap={1} horizontal>
              <Box
                css={{
                  whiteSpace: "nowrap",
                }}
              >
                Forked from
              </Box>
              <Box
                css={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",

                  a: {
                    color:
                      "var(--vscode-textLink-foreground, $neutral-fg-subtle)",

                    "&:hover": {
                      color: "$accent-primary-base",
                    },
                  },
                }}
              >
                <a
                  href={withBasePath(
                    env,
                    paths.editor.sandbox(forkedFromSandbox.id),
                  )}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {forkedFromSandbox?.title || forkedFromSandbox?.alias}
                </a>
              </Box>
            </Stack>
          ) : null}

          {/* Forked info, only show this one if forked from template, and this is not a template. */}
          {!forkedFromSandbox &&
          sandbox.customTemplate == null &&
          forkedTemplate &&
          forkedTemplateSandbox ? (
            <Stack gap={1} horizontal>
              <Box
                css={{
                  whiteSpace: "nowrap",
                }}
              >
                Made with
              </Box>
              <Box
                css={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",

                  a: {
                    color:
                      "var(--vscode-textLink-foreground, $neutral-fg-subtle)",

                    "&:hover": {
                      color: "$accent-primary-base",
                    },
                  },
                }}
              >
                <a
                  href={withBasePath(
                    env,
                    paths.editor.sandbox(forkedTemplateSandbox.id),
                  )}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {forkedTemplate?.title}
                </a>
              </Box>
            </Stack>
          ) : null}
        </Stack>
      )}

      <Box>
        <StyledDL>
          {/* Views */}
          {!small ? (
            <>
              <StyledDItem>
                <StyledDT>
                  <Icon size={4} type="eyeOpen" />
                </StyledDT>
                <StyledDD>{numberToSymbol(sandbox.viewCount)}</StyledDD>
              </StyledDItem>

              {/* Forks */}
              <StyledDItem>
                <StyledDT>
                  <Icon size={4} type="gitFork" />
                </StyledDT>
                <StyledDD>{numberToSymbol(sandbox.forkCount)}</StyledDD>
              </StyledDItem>
            </>
          ) : null}

          {sandbox.team && (
            <StyledDItem>
              <StyledAvatar
                avatarSrc={sandbox.team.avatarUrl}
                name={sandbox.team.name}
              />
              <Text
                css={{
                  color:
                    "var(--vscode-descriptionForeground, $neutral-fg-subtle)",
                }}
              >
                {sandbox.team.name}
              </Text>
            </StyledDItem>
          )}

          {/* Show author name if no sandbox team */}
          {sandbox.team === null && sandbox.author && (
            <StyledDItem>
              <StyledDT>
                <StyledAvatar
                  avatarSrc={sandbox.author.avatarUrl}
                  name={sandbox.author.name || sandbox.author.username}
                />
              </StyledDT>
              <StyledA
                href={paths.userProfile(sandbox.author.username)}
                target="_blank"
              >
                {sandbox.author.name || sandbox.author.username}
              </StyledA>
            </StyledDItem>
          )}
        </StyledDL>
      </Box>
    </Box>
  );
};

/**
 * Returns the number formatted to show the k and M symbols when necessary.
 */
export function numberToSymbol(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }

  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }

  return `${count}`;
}
